<script setup lang="ts">
import { type Component } from 'vue';

import date from '~/utils/date';

interface Props {
  datetime?: Date | string;
  icon?: Component;
  title: string;
  type?: 'default' | 'informative' | 'negative' | 'positive' | 'warning';
}

const props = withDefaults(defineProps<Props>(), {
  datetime: undefined,
  icon: undefined,
  type: 'default',
});

const typeColors = {
  default: {
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    border: '#adb5bd',
    iconBg: '#6c757d',
    label: '#495057',
    value: '#212529',
  },
  informative: {
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    border: '#0054a6',
    iconBg: '#0054a6',
    label: '#1e40af',
    value: '#1d4ed8',
  },
  negative: {
    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    border: '#dc2626',
    iconBg: '#dc2626',
    label: '#991b1b',
    value: '#b91c1c',
  },
  positive: {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    border: '#2fb344',
    iconBg: '#2fb344',
    label: '#166534',
    value: '#15803d',
  },
  warning: {
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    border: '#f59e0b',
    iconBg: '#f59e0b',
    label: '#92400e',
    value: '#b45309',
  },
};

const currentColors = typeColors[props.type];
</script>

<template>
  <div class="status-card" :style="{ 'border-color': currentColors.border, background: currentColors.background }">
    <div v-if="icon" class="icon" :style="{ background: currentColors.iconBg }">
      <component :is="icon" :size="24" :stroke-width="2" />
    </div>
    <div class="content">
      <div class="label" :style="{ color: currentColors.label }">
        {{ title }}
      </div>
      <div v-if="datetime" class="value" :style="{ color: currentColors.value }">
        {{ date(datetime).format('DD/MM/YYYY') }}
      </div>
      <div v-else class="value" :style="{ color: currentColors.value }">
        -
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.status-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: white;
  border: 1px solid;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 0.5rem;
    flex-shrink: 0;
    color: white;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: flex-start;
    flex: 1;

    .label {
      font-size: 0.8rem;
      font-weight: 600;
    }

    .value {
      font-size: 0.875rem;
      font-weight: 700;
      line-height: 1;
    }

    .time {
      font-size: 0.8125rem;
      opacity: 0.75;
    }
  }
}
</style>
